import { memo, useMemo } from 'react';

import { type PresentationContentLinksBlockOutput } from '@air/api/types';
import { type ShortIdProviderProps } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { PublicShowcaseLinksBlockCard } from '~/components/v2/components/showcase/PublicShowcaseLinksBlockCard';
import { PublicShowcaseLinksBlockPill } from '~/components/v2/components/showcase/PublicShowcaseLinksBlockPill';
import { getBrandedButtonStyle } from '~/components/v2/utils/filters/getContrastColor';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';

type PresentationBlock = NonNullable<ShortIdProviderProps['presentationContent']>['blocks'][number];

const LINKS_BLOCK_ALIGNMENT_CLASSES: Record<PresentationContentLinksBlockOutput['alignment'], string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

// Defensive render-time check (same philosophy as the text block's link mapping): only http/https
// destinations render as anchors — anything else (javascript:, data:, malformed) renders as a
// non-clickable pill/card instead.
const getSafeLinkHref = (url: string): string | undefined => {
  try {
    const { protocol } = new URL(url);

    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

export type PublicShowcaseLinksBlockProps = {
  block: PresentationBlock;
};

export const PublicShowcaseLinksBlock = memo(({ block }: PublicShowcaseLinksBlockProps) => {
  const { buttonBgColor } = usePublicCustomColors();
  const { areExternalLinksClickable } = useExternalLinksClickableContext();

  const brandedStyle = useMemo(
    () => (buttonBgColor ? getBrandedButtonStyle(buttonBgColor) : undefined),
    [buttonBgColor],
  );

  const links = block.links ?? [];

  if (links.length === 0) {
    return null;
  }

  // The envelope models the payload fields as optional (one structural shape covers every block
  // variant) — the wire guarantees them on links blocks, so the fallbacks only cover skew. An
  // unknown runtime `style` degrades to the simpler pill row; an unknown `alignment` resolves to
  // no class → the flex default (left).
  const style = block.style ?? 'pill';
  const size = block.size ?? 'medium';
  const alignmentClass: string | undefined = LINKS_BLOCK_ALIGNMENT_CLASSES[block.alignment ?? 'left'];

  return (
    <div
      className={cn('flex w-full flex-wrap gap-2 px-4 py-3', alignmentClass)}
      data-block-id={block.id}
      data-testid="PUBLIC_COLLECTION_LINKS_BLOCK"
    >
      {links.map((link) => {
        const href = areExternalLinksClickable ? getSafeLinkHref(link.url) : undefined;

        if (style === 'card') {
          return (
            <PublicShowcaseLinksBlockCard
              href={href}
              key={link.id}
              label={link.label}
              linkId={link.id}
              placeholderColor={buttonBgColor}
              size={size}
              thumbnailUrl={link.thumbnail?.url}
            />
          );
        }

        return (
          <PublicShowcaseLinksBlockPill
            brandedStyle={brandedStyle}
            href={href}
            key={link.id}
            label={link.label}
            linkId={link.id}
            size={size}
          />
        );
      })}
    </div>
  );
});

PublicShowcaseLinksBlock.displayName = 'PublicShowcaseLinksBlock';
